<template>
  <v-card>
    <v-card-title class="d-flex align-center">
      <span style="flex: 1; min-width: 0">{{ $t('rights.addForm.title') }}</span>
      <v-btn v-if="!$vuetify.breakpoint.mobile" text @click="cancel">{{ $t('app.cancel') }}</v-btn>
    </v-card-title>

    <v-stepper
      v-model="currentStep"
      vertical
    >

      <v-stepper-step
        :complete="currentStep > 1"
        :step="1"
        editable
      >

        <span style="margin-bottom: 4px;">{{ $t('rights.addForm.isrc.title') }} </span>
        <small>{{ $t('rights.addForm.isrc.subtitle') }}</small>

      </v-stepper-step>


      <v-stepper-content :step="1">
        <v-container>
          <v-row>
            <v-col cols="6">
              <v-text-field
                :label="$t('rights.addForm.isrc.label')"
                v-model="currentRight.isrc"
                :rules="isrcRules"
              ></v-text-field>
            </v-col>
            <v-col cols="6">
              <v-select
                :items="[{value: 1, text: $t('rights.types.audio')}, {value: 2, text:  $t('rights.types.video')}]"
                :label="$t('rights.addForm.type.label')"
                v-model="currentRight.type"
                :rules="typeRules"
              ></v-select>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="auto" v-show="!searchLoading">
              <v-btn
                color="primary"
                @click="onMoveSecondStep"
                :disabled="firstStepDisabled"
              > {{ $t('rights.addForm.continue') }}
              </v-btn>
            </v-col>
            <v-col cols="12" v-show="searchLoading">
              <v-text-field loading disabled color="primary"/>
            </v-col>
          </v-row>
        </v-container>
      </v-stepper-content>

      <v-stepper-step
        :complete="currentStep > 2"
        :step="2"
      >
        <span style="margin-bottom: 4px;">{{ $t('rights.addForm.step2.title') }}</span>
        <small>{{ $t('rights.addForm.step2.subtitle') }}</small>
      </v-stepper-step>

      <v-stepper-content :step="2">
        <v-container>
          <v-row>
            <v-col cols="6">

              <v-text-field
                :label="$t('rights.addForm.isrc.label')"
                v-model="currentRight.isrc"
                readonly
                disabled></v-text-field>


              <v-text-field
                :label="$t('rights.columns.externalId')"
                v-model="currentRight.externalId"></v-text-field>

              <v-text-field :label="$t('rights.columns.artistName')" v-model="currentRight.artistName"></v-text-field>
              <v-text-field :label="$t('rights.columns.title')" v-model="currentRight.title"></v-text-field>
              <v-text-field :label="$t('rights.columns.labelName')" v-model="currentRight.labelName"></v-text-field>
              <v-text-field :label="$t('rights.columns.year')" v-model="currentRight.year"></v-text-field>
              <v-autocomplete
                class="mt-n2"
                clearable
                v-model="currentRight.countryCode"
                :items="allCountries"
                :label="$t('rights.columns.countryCode')"
              ></v-autocomplete>
              <v-autocomplete
                class="mt-n2"
                clearable
                v-model="currentRight.companyCountryCode"
                :items="allCountries"
                :label="$t('rights.columns.companyCountryCode')"
              ></v-autocomplete>
              <v-autocomplete
                class="mt-n2"
                clearable
                v-model="currentRight.secondaryCountryCode"
                :items="allCountries"
                :label="$t('rights.columns.secondaryCountryCode')"
              ></v-autocomplete>

              <v-text-field :label="$t('rights.columns.feat')" v-model="currentRight.feat"></v-text-field>
              <v-text-field :label="$t('rights.columns.searchTerms')" v-model="currentRight.searchTerms"></v-text-field>

            </v-col>

            <v-col cols="6">
              <v-switch :label="$t('rights.columns.revokeCol')" v-model="currentRight.revoke"></v-switch>

              <template v-if="!currentRight.revoke">

                <DatePickerField :label="$t('rights.columns.claimStartDate')" v-model="currentRight.claimStartDate"/>
                <DatePickerField v-if="!currentRight.noEnd" :label="$t('rights.columns.claimEndDate')"
                                 v-model="currentRight.claimEndDate"/>
                <v-switch :label="$t('rights.columns.noEnd')" v-model="currentRight.noEnd"></v-switch>
              </template>

              <v-text-field :label="$t('rights.columns.version')" v-model="currentRight.version"></v-text-field>
              <v-text-field :label="$t('rights.columns.duration')" v-model="currentRight.duration"></v-text-field>

              <v-switch :label="$t('rights.columns.isClassical')" v-model="currentRight.isClassical"></v-switch>
              <v-text-field :label="$t('rights.columns.composer')" v-model="currentRight.composer"></v-text-field>
              <v-switch
                :label="$t('rights.columns.internationalCollection')"
                v-model="currentRight.internationalCollection"
                v-if="$store.getters['app/currentRightsholder'].internationalCollectionAllowed"
              ></v-switch>

            </v-col>
          </v-row>

          <v-row>
            <v-col cols="auto">
              <v-btn color="primary" @click="onSave">{{ $t('rights.addForm.save') }}</v-btn>
            </v-col>
          </v-row>
        </v-container>

      </v-stepper-content>

    </v-stepper>
  </v-card>
</template>

<script>
import DatePickerField from "@/components/common/ui/DatePickerField";
import Right from "@/models/Right";
import loGet from 'lodash/get';
import countries from 'i18n-iso-countries'
import {mapGetters} from 'vuex'

export default {
  name: "AddRightForm",
  components: {DatePickerField},
  data() {
    return {
      searchLoading: false,
      currentStep: 1,
      currentRight: new Right(),
      isrcRules: [
        value => (/^[A-Za-z]{2}-?\w{3}-?\d{2}-?\d{5}$/).test(value) || this.$t('rights.addForm.isrc.rules.chars')
      ],
      typeRules: [
        value => [1, 2].includes(value) || this.$t('rights.addForm.type.rules.empty')
      ]
    }
  },

  computed: {
    ...mapGetters('app', ['locale']),

    firstStepDisabled() {
      const isrc = this.currentRight.isrc;
      const type = this.currentRight.type;
      return !(this.isrcRules.every((f) => f(isrc) === true) && this.typeRules.every((f) => f(type) === true));
    },

    allCountries() {
      countries.registerLocale(require(`i18n-iso-countries/langs/${this.locale}.json`))
      const allC = Object.entries(countries.getNames(this.locale)).map(([k,v]) => {
        return {value: k, text: `${k} - ${v}`}
      })
      return allC
    },
  },

  methods: {
    onMoveSecondStep() {
      this.searchLoading = true;
      this.$store.dispatch('app/addRightsToStaging', [this.currentRight])
        .then((list) => {
          if (list.length) {
            if ((loGet(list[0], 'errflg', '0') == 1 && loGet(list[0], 'processing-status', '0') == 422)) {
              this.$toast(loGet(list[0], `errormsg[${this.locale}]`, 'An RDX problem prevents adding this data to staging'));
            } else {
              this.currentRight = new Right(list[0]);
              this.currentStep = 2;
            }
          }
        })
        .finally(() => {
          this.searchLoading = false;
        })
    },

    onSave() {
      this.$emit('save', this.currentRight)
    },

    cancel() {
      this.$store.dispatch('app/removeSingleStagingRow', this.currentRight);
      this.$emit('cancel');
    },
  },

  mounted() {
    this.currentRight = new Right();
  }
}
</script>

<style scoped>
.v-stepper, .v-stepper__content, .v-stepper__wrapper {
  background-color: transparent !important;
  box-shadow: none !important;
}
</style>
